import React, { useState } from 'react';
import { Order, NextPaymentResponse } from '../../models/APIModels';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/24/solid';
import Moment from 'moment';
import { toMoney } from '../../utilities/MoneyConversion';
import { getItemsInStorage, getOrderTypeLabel, getOrderStatus } from '../../utilities/OrderHelpers';
import { downloadOrderLabels } from '../../utilities/PdfDownload';
import { isEmptyValue } from '../../utilities/ValueHelpers';
import {
  getOrderProgressSteps,
  isOrderCancelled,
  getCancelledInfo,
  ProgressStep,
} from '../../utilities/OrderProgressSteps';

interface ActiveOrderCardProps {
  order: Order;
  nextPaymentData?: NextPaymentResponse;
  isExpanded: boolean;
  onToggle: () => void;
  onRequestReturn?: (e: React.MouseEvent) => void;
  onViewDetails: (e?: React.MouseEvent) => void;
}

const SectionHeading: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <h4>
    <span className="order-detail-section__badge" aria-hidden="true">{icon}</span>
    <span className="order-detail-section__title">{title}</span>
  </h4>
);

const JourneyStepRow: React.FC<{ step: ProgressStep; isLast: boolean }> = ({ step, isLast }) => {
  const showTimestamp =
    step.state === 'completed' && typeof step.timestamp === 'number' && step.timestamp > 0;
  const showSublabel = (step.state === 'current' || step.state === 'failed') && !!step.sublabel;

  return (
    <li className={`order-journey__step order-journey__step--${step.state}`}>
      <div className="order-journey__rail">
        <span className="order-journey__indicator" aria-hidden="true">
          {step.state === 'completed' && (
            <CheckIcon className="order-journey__mark order-journey__mark--check" />
          )}
          {step.state === 'current' && <span className="order-journey__pulse" />}
          {step.state === 'failed' && <span className="order-journey__mark order-journey__mark--fail">!</span>}
          {step.state === 'future' && <span className="order-journey__dot" />}
        </span>
        {!isLast && <span className="order-journey__connector" aria-hidden="true" />}
      </div>
      <div className="order-journey__body">
        <div className="order-journey__heading">
          <div className="order-journey__heading-left">
            <span className="order-journey__icon" aria-hidden="true">{step.icon}</span>
            <span className="order-journey__label">{step.label}</span>
            {step.state === 'current' && <span className="order-journey__tag">Now</span>}
          </div>
          {showTimestamp && (
            <span className="order-journey__timestamp">
              {Moment(step.timestamp).format('D MMM YYYY')}
            </span>
          )}
        </div>
        {showSublabel && <p className="order-journey__sublabel">{step.sublabel}</p>}
      </div>
    </li>
  );
};

const OrderJourney: React.FC<{ order: Order; nextPayment?: NextPaymentResponse }> = ({
  order,
  nextPayment,
}) => {
  if (isOrderCancelled(order)) {
    const { cancelledAt } = getCancelledInfo(order);
    return (
      <div className="order-detail-section order-detail-section--cancelled">
        <SectionHeading icon="⚠️" title="ORDER CANCELLED" />
        <p className="order-journey__cancelled-body">
          {cancelledAt
            ? `Cancelled on ${Moment(cancelledAt).format('D MMM YYYY')}`
            : 'This order is no longer active.'}
        </p>
      </div>
    );
  }

  const steps = getOrderProgressSteps(order, nextPayment);
  if (!steps || steps.length === 0) return null;

  return (
    <div className="order-detail-section order-detail-section--journey">
      <SectionHeading icon="🧭" title="ORDER JOURNEY" />
      <ol className="order-journey">
        {steps.map((step, idx) => (
          <JourneyStepRow key={step.id} step={step} isLast={idx === steps.length - 1} />
        ))}
      </ol>
    </div>
  );
};

export const ActiveOrderCard: React.FC<ActiveOrderCardProps> = ({
  order,
  nextPaymentData,
  isExpanded,
  onToggle,
  onRequestReturn,
  onViewDetails,
}) => {
  const [isDownloadingLabels, setIsDownloadingLabels] = useState(false);
  const isRemovalOrder = order.orderType === 'removal';
  const isShippingOrder = !isRemovalOrder && !order.storageRequired && !order.suitcaseOrder;

  const itemsCount = isRemovalOrder ? 0 : getItemsInStorage(order);
  const orderTypeLabel = getOrderTypeLabel(order);
  const currentStatus = getOrderStatus(order);

  const monthlyCost = isRemovalOrder
    ? (order.monthlyStorageCost || 0)
    : (order.monthlyCost || 0);

  const handleDownloadLabels = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDownloadingLabels(true);
    try {
      await downloadOrderLabels(order.id);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download label. Please try again or contact support.');
    } finally {
      setIsDownloadingLabels(false);
    }
  };

  return (
    <div className="active-order-card">
      <div className="active-order-header" onClick={onToggle}>
        <div className="active-order-info">
          {isRemovalOrder ? (
            <>
              <div className="order-info-item">
                <span className="order-info-icon">🚚</span>
                <span className="order-info-text">{order.tierName} Move</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-icon">📋</span>
                <span className="order-info-text">Removal Order {order.id}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-icon">💳</span>
                <span className="order-info-text">
                  {order.storageId ? (
                    nextPaymentData 
                      ? `Next Payment: ${toMoney(nextPaymentData.amount)} due ${Moment(nextPaymentData.nextPaymentDate).format('MMM D')}`
                      : `Storage: ${toMoney(monthlyCost)}/mo`
                  ) : (
                    'One-time move'
                  )}
                </span>
              </div>
            </>
          ) : isShippingOrder ? (
            <>
              <div className="order-info-item">
                <span className="order-info-icon">📮</span>
                <span className="order-info-text">{itemsCount} items shipped</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-icon">📋</span>
                <span className="order-info-text">Shipping Order {order.id}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-icon">💳</span>
                <span className="order-info-text">
                  Total: {toMoney(order.initialCost || 0)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="order-info-item">
                <span className="order-info-icon">📦</span>
                <span className="order-info-text">Items in storage: {itemsCount}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-icon">📋</span>
                <span className="order-info-text">{orderTypeLabel} Order {order.id}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-icon">💳</span>
                <span className="order-info-text">
                  Next Payment: {nextPaymentData 
                    ? `${toMoney(nextPaymentData.amount)} due ${Moment(nextPaymentData.nextPaymentDate).format('MMM D')}`
                    : 'No upcoming payment'}
                </span>
              </div>
            </>
          )}
        </div>
        <span className="order-expand-icon">
          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </span>
      </div>
      
      {isExpanded && (
        <div className="active-order-details">
          {isRemovalOrder ? (
            <div className="order-details-grid">
              <OrderJourney order={order} nextPayment={nextPaymentData} />
              <div className="order-details-grid__side">
                <div className="order-detail-section">
                  <SectionHeading icon="📍" title="ADDRESSES" />
                  <div className="timeline-items">
                    <div className="timeline-item">
                      <span className="timeline-icon">📦</span>
                      <span className="timeline-label">Collection Address</span>
                      <span className="timeline-value">
                        {order.collectionAddress || 'N/A'}
                      </span>
                    </div>
                    {!isEmptyValue(order.deliveryAddress) && (
                      <div className="timeline-item">
                        <span className="timeline-icon">🚚</span>
                        <span className="timeline-label">Delivery Address</span>
                        <span className="timeline-value">
                          {order.deliveryAddress}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="order-detail-section">
                  <SectionHeading icon="🏷️" title="REMOVAL DETAILS" />
                  <div className="storage-summary">
                    <div className="summary-row">
                      <span className="summary-label">{order.tierName} Tier</span>
                      <span className="summary-value">{toMoney(order.tierPrice || 0)}</span>
                      <span className="summary-status" style={{textTransform: 'capitalize'}}>
                        {currentStatus}
                      </span>
                    </div>
                    {order.packingMaterialsId && (
                      <div className="summary-row">
                        <span className="summary-label">Packing Materials</span>
                        <span className="summary-value">{toMoney(order.packingMaterialsPrice || 0)}</span>
                      </div>
                    )}
                    {order.storageId && (
                      <div className="summary-row">
                        <span className="summary-label">Storage ({order.storageSize})</span>
                        <span className="summary-value">{toMoney(order.monthlyStorageCost || 0)}/mo</span>
                      </div>
                    )}
                  </div>
                </div>

                {(order.stripeSubscriptionId || (nextPaymentData && order.storageId)) && (
                  <div className="order-detail-section">
                    <SectionHeading icon="🔁" title="STORAGE PAYMENTS" />
                    <div className="payment-info">
                      <div className="payment-row">
                        <span className="payment-label">
                          Next charge: {nextPaymentData
                            ? Moment(nextPaymentData.nextPaymentDate).format('D MMMM YYYY')
                            : 'N/A'}
                          {nextPaymentData && nextPaymentData.status !== 'active' && (
                            <span className="payment-status" style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: nextPaymentData.status === 'past_due' ? '#ff5757' : '#ffa500',
                              color: 'white'
                            }}>
                              {nextPaymentData.status === 'past_due' ? '⚠️ Payment Failed' :
                               nextPaymentData.status === 'trialing' ? 'Trial Period' :
                               nextPaymentData.status}
                            </span>
                          )}
                        </span>
                        <span className="payment-amount">
                          {nextPaymentData ? toMoney(nextPaymentData.amount) : toMoney(monthlyCost)}
                        </span>
                      </div>
                      <div className="payment-action">
                        <span className="payment-link" onClick={onViewDetails}>
                          View Order Details →
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {!(order.stripeSubscriptionId || (nextPaymentData && order.storageId)) && (
                  <div className="order-detail-section">
                    <div className="payment-action">
                      <span className="payment-link" onClick={onViewDetails}>
                        View Order Details →
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : isShippingOrder ? (
            <div className="order-details-grid">
              <OrderJourney order={order} nextPayment={nextPaymentData} />
              <div className="order-details-grid__side">
                <div className="order-detail-section">
                  <SectionHeading icon="📍" title="ADDRESSES" />
                  <div className="timeline-items">
                    <div className="timeline-item">
                      <span className="timeline-icon">📦</span>
                      <span className="timeline-label">Collection Address</span>
                      <span className="timeline-value">
                        {order.collectionAddress || 'N/A'}
                      </span>
                    </div>
                    {!isEmptyValue(order.deliveryAddress) && (
                      <div className="timeline-item">
                        <span className="timeline-icon">🚚</span>
                        <span className="timeline-label">Delivery Address</span>
                        <span className="timeline-value">
                          {order.deliveryAddress}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="order-detail-section">
                  <SectionHeading icon="💳" title="PAYMENT" />
                  <div className="payment-info">
                    <div className="payment-row">
                      <span className="payment-label">
                        Order Total
                      </span>
                      <span className="payment-amount">
                        {toMoney(order.initialCost || 0)}
                      </span>
                    </div>
                    <div className="payment-action">
                      <span className="payment-link" onClick={onViewDetails}>
                        View Order Details →
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="order-details-grid">
              <OrderJourney order={order} nextPayment={nextPaymentData} />
              <div className="order-details-grid__side">
                <div className="order-detail-section">
                  <SectionHeading icon="📍" title="ADDRESSES" />
                  <div className="timeline-items">
                    <div className="timeline-item">
                      <span className="timeline-icon">📦</span>
                      <span className="timeline-label">Collection Address</span>
                      <span className="timeline-value">
                        {order.collectionAddress || 'N/A'}
                      </span>
                    </div>
                    {!isEmptyValue(order.deliveryAddress) && (
                      <div className="timeline-item">
                        <span className="timeline-icon">🚚</span>
                        <span className="timeline-label">Delivery Address</span>
                        <span className="timeline-value">
                          {order.deliveryAddress}
                        </span>
                      </div>
                    )}
                  </div>
                  {order.storageRequired && !order.suitcaseOrder && isEmptyValue(order.deliveryDate) && (
                    <div className="timeline-action">
                      <span className="timeline-link" onClick={onRequestReturn}>
                        Request Return →
                      </span>
                    </div>
                  )}
                </div>

                <div className="order-detail-section">
                  <SectionHeading icon="📦" title="YOUR STORAGE SUMMARY" />
                  <div className="storage-summary">
                    <div className="summary-row">
                      <span className="summary-label">{itemsCount} items stored</span>
                      <span className="summary-value">{toMoney(monthlyCost)}/mo</span>
                      <span className="summary-status">
                        {currentStatus}
                      </span>
                    </div>
                  </div>
                  <div className="storage-action">
                    <button
                      className="download-labels-btn"
                      onClick={handleDownloadLabels}
                      disabled={isDownloadingLabels}
                      style={{
                        opacity: isDownloadingLabels ? 0.6 : 1,
                        cursor: isDownloadingLabels ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isDownloadingLabels ? 'Downloading...' : '📥 Download All Labels (PDF)'}
                    </button>
                  </div>
                </div>

                <div className="order-detail-section">
                  <SectionHeading icon="💳" title="PAYMENTS" />
                  <div className="payment-info">
                    <div className="payment-row">
                      <span className="payment-label">
                        Next charge: {nextPaymentData
                          ? Moment(nextPaymentData.nextPaymentDate).format('D MMMM YYYY')
                          : 'N/A'}
                        {nextPaymentData && nextPaymentData.status !== 'active' && (
                          <span className="payment-status" style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: nextPaymentData.status === 'past_due' ? '#ff5757' : '#ffa500',
                            color: 'white'
                          }}>
                            {nextPaymentData.status === 'past_due' ? '⚠️ Payment Failed' :
                             nextPaymentData.status === 'trialing' ? 'Trial Period' :
                             nextPaymentData.status}
                          </span>
                        )}
                      </span>
                      <span className="payment-amount">
                        {nextPaymentData ? toMoney(nextPaymentData.amount) : toMoney(0)}
                      </span>
                    </div>
                    <div className="payment-action">
                      <span className="payment-link" onClick={onViewDetails}>
                        View Order Details →
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

